<template>
  <div class="pa">
    <div v-if="loading" class="pa-muted"><b-spinner small /> Loading…</div>
    <template v-else>
      <b-alert show variant="warning" v-if="!settings.emailsSwitchedOn" class="pa-alert">
        Email sending is switched off on this server, so no alert emails will go out from here.
      </b-alert>

      <div class="pa-row">
        <div>
          <strong>Email me when a payment fails</strong>
          <div class="pa-muted">Consultation payments, additional payments (documents, delivery, medication).
            At most one email per order per hour.</div>
        </div>
        <b-form-checkbox switch size="lg" :checked="settings.enabled" :disabled="saving" @change="save({ enabled: $event })" />
      </div>

      <div class="pa-row">
        <div>
          <strong>Also email for declined cards at checkout</strong>
          <div class="pa-muted">When a patient's card is declined while booking (before an order exists).
            One email per checkout per hour.</div>
        </div>
        <b-form-checkbox switch :checked="settings.includeCheckout" :disabled="saving" @change="save({ includeCheckout: $event })" />
      </div>

      <div class="pa-label">Who gets the emails</div>
      <div class="pa-emails">
        <span v-for="(email, idx) in settings.emails" :key="email" class="pa-pill">
          {{ email }}
          <button type="button" class="pa-x" :disabled="saving" :title="'Remove ' + email" @click="removeEmail(idx)">×</button>
        </span>
        <span v-if="!settings.emails.length" class="pa-muted">Nobody yet — add an email below.</span>
      </div>
      <b-input-group size="sm" class="pa-add">
        <b-form-input v-model="newEmail" type="email" placeholder="name@thegpservice.co.uk" @keyup.enter="addEmail" />
        <b-input-group-append>
          <b-button variant="primary" :disabled="saving || !newEmail" @click="addEmail">Add</b-button>
        </b-input-group-append>
      </b-input-group>

      <div class="pa-foot">
        <span class="pa-muted" v-if="settings.updatedAt">Changes save straight away · last change {{ when(settings.updatedAt) }}</span>
        <span class="pa-muted" v-else>Changes save straight away</span>
        <b-button size="sm" variant="outline-secondary" :disabled="saving || !settings.emails.length" @click="sendTest">
          <i class="fa fa-paper-plane-o"></i> Send test
        </b-button>
      </div>
    </template>
  </div>
</template>

<script>
import moment from 'moment';

const EMAIL = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

export default {
  data () {
    return {
      loading: true,
      saving: false,
      newEmail: '',
      settings: { enabled: false, includeCheckout: true, emails: [], emailsSwitchedOn: true }
    };
  },
  created () {
    this.$orderService.getPaymentAlertSettings().then((res) => {
      this.apply(res);
    }).catch((error) => {
      this.$awn.alert(this.errorMessage(error, 'Could not load alert settings'));
    }).finally(() => {
      this.loading = false;
    });
  },
  methods: {
    apply (res) {
      const data = (res && res.data) || {};
      this.settings = {
        enabled: !!data.enabled,
        includeCheckout: data.includeCheckout !== false,
        emails: data.emails || [],
        emailsSwitchedOn: data.emailsSwitchedOn !== false,
        updatedAt: data.updatedAt
      };
    },
    save (change) {
      this.saving = true;
      return this.$orderService.savePaymentAlertSettings(change).then((res) => {
        this.apply(res);
      }).catch((error) => {
        this.$awn.alert(this.errorMessage(error, 'Could not save alert settings'));
        // Put the switches back to what the server has.
        return this.$orderService.getPaymentAlertSettings().then(this.apply).catch(() => {});
      }).finally(() => {
        this.saving = false;
      });
    },
    addEmail () {
      const email = (this.newEmail || '').trim().toLowerCase();
      if (!EMAIL.test(email)) {
        this.$awn.warning('That doesn\'t look like an email address');
        return;
      }
      if (this.settings.emails.indexOf(email) >= 0) {
        this.newEmail = '';
        return;
      }
      this.save({ emails: this.settings.emails.concat(email) }).then(() => { this.newEmail = ''; });
    },
    removeEmail (idx) {
      const emails = this.settings.emails.filter((_, i) => i !== idx);
      // Nobody left to email means alerts must go off too.
      this.save(emails.length ? { emails } : { emails, enabled: false });
    },
    sendTest () {
      this.saving = true;
      this.$orderService.sendPaymentAlertTest().then(() => {
        this.$awn.success('Test email sent to ' + this.settings.emails.join(', '));
      }).catch((error) => {
        this.$awn.alert(this.errorMessage(error, 'Could not send the test email'));
      }).finally(() => {
        this.saving = false;
      });
    },
    when (value) {
      return value ? moment(value).local().format('DD/MM/YY HH:mm') : '';
    },
    errorMessage (error, fallback) {
      const data = error && error.response && error.response.data;
      return (data && (data.message || data.error)) || fallback;
    }
  }
};
</script>

<style scoped>
.pa-row {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 1rem;
  padding: 0.75rem 0;
  border-bottom: 1px solid #e5ebe8;
}
.pa-muted {
  color: #64748b;
  font-size: 0.85rem;
}
.pa-alert {
  font-size: 0.85rem;
}
.pa-label {
  font-weight: 600;
  margin: 1rem 0 0.5rem;
}
.pa-emails {
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
  margin-bottom: 0.6rem;
}
.pa-pill {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  background: #e7f4f7;
  color: #0f6a7c;
  border-radius: 999px;
  padding: 0.2rem 0.4rem 0.2rem 0.7rem;
  font-size: 0.85rem;
}
.pa-x {
  border: 0;
  background: transparent;
  color: inherit;
  font-size: 1rem;
  line-height: 1;
  cursor: pointer;
}
.pa-foot {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
  margin-top: 1rem;
  flex-wrap: wrap;
}
</style>
